import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  MapPin, 
  Video,
  Users,
  AlertTriangle,
  Check,
  X
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isSameDay, isToday, addDays, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMeetingStore } from '../../store/meetingStore';
import { useAuthStore } from '../../store/authStore';
import type { Meeting } from '../../types/meetings';

interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
  meeting?: Meeting;
}

interface AvailabilitySlot {
  day: number; // 0-6 (Sunday-Saturday)
  start: string; // HH:mm
  end: string; // HH:mm
}

export const AdvancedCalendar: React.FC = () => {
  const { user } = useAuthStore();
  const { meetings, fetchMeetings, createMeeting } = useMeetingStore();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [view, setView] = useState<'week' | 'month'>('week');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([
    { day: 1, start: '09:00', end: '12:00' }, // Lundi matin
    { day: 1, start: '14:00', end: '17:00' }, // Lundi après-midi
    { day: 2, start: '09:00', end: '12:00' }, // Mardi matin
    { day: 2, start: '14:00', end: '17:00' }, // Mardi après-midi
    { day: 3, start: '09:00', end: '12:00' }, // Mercredi matin
    { day: 4, start: '09:00', end: '12:00' }, // Jeudi matin
    { day: 4, start: '14:00', end: '17:00' }, // Jeudi après-midi
    { day: 5, start: '09:00', end: '12:00' }, // Vendredi matin
  ]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const previousWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const nextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));

  const getMeetingsForDay = (date: Date) => {
    return meetings.filter(meeting => 
      isSameDay(new Date(meeting.start_date), date)
    );
  };

  const getTimeSlots = (date: Date): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const dayOfWeek = date.getDay();
    const dayAvailability = availability.filter(slot => slot.day === dayOfWeek);
    const dayMeetings = getMeetingsForDay(date);

    // Generate hourly slots for available periods
    dayAvailability.forEach(avail => {
      const startHour = parseInt(avail.start.split(':')[0]);
      const endHour = parseInt(avail.end.split(':')[0]);
      
      for (let hour = startHour; hour < endHour; hour++) {
        const slotStart = `${hour.toString().padStart(2, '0')}:00`;
        const slotEnd = `${(hour + 1).toString().padStart(2, '0')}:00`;
        
        // Check if there's a meeting in this slot
        const meeting = dayMeetings.find(m => {
          const meetingStart = new Date(m.start_date);
          const meetingHour = meetingStart.getHours();
          return meetingHour === hour;
        });

        slots.push({
          start: slotStart,
          end: slotEnd,
          available: !meeting,
          meeting
        });
      }
    });

    return slots.sort((a, b) => a.start.localeCompare(b.start));
  };

  const hasConflicts = () => {
    return meetings.some(meeting => {
      const meetingDate = new Date(meeting.start_date);
      const dayOfWeek = meetingDate.getDay();
      const meetingHour = meetingDate.getHours();
      
      return !availability.some(slot => {
        if (slot.day !== dayOfWeek) return false;
        const startHour = parseInt(slot.start.split(':')[0]);
        const endHour = parseInt(slot.end.split(':')[0]);
        return meetingHour >= startHour && meetingHour < endHour;
      });
    });
  };

  const getWeekStats = () => {
    const weekMeetings = meetings.filter(meeting => {
      const meetingDate = new Date(meeting.start_date);
      return meetingDate >= weekStart && meetingDate <= weekEnd;
    });

    return {
      totalMeetings: weekMeetings.length,
      confirmedMeetings: weekMeetings.filter(m => m.status === 'CONFIRME').length,
      proposedMeetings: weekMeetings.filter(m => m.status === 'PLANIFIE').length,
      totalHours: weekMeetings.reduce((sum, m) => {
        const duration = (new Date(m.end_date).getTime() - new Date(m.start_date).getTime()) / (1000 * 60 * 60);
        return sum + duration;
      }, 0)
    };
  };

  const weekStats = getWeekStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendrier Professionnel</h1>
          <p className="text-gray-600 mt-1">
            Gestion avancée de votre planning d'accompagnement
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAvailabilityModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Clock className="w-4 h-4" />
            <span>Disponibilités</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <Plus className="w-4 h-4" />
            <span>Nouveau RDV</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Cette semaine</p>
              <p className="text-2xl font-bold text-gray-900">{weekStats.totalMeetings}</p>
              <p className="text-xs text-gray-500">rendez-vous</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Confirmés</p>
              <p className="text-2xl font-bold text-green-600">{weekStats.confirmedMeetings}</p>
              <p className="text-xs text-gray-500">sur {weekStats.totalMeetings}</p>
            </div>
            <Check className="w-8 h-8 text-green-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Temps total</p>
              <p className="text-2xl font-bold text-gray-900">{weekStats.totalHours.toFixed(1)}h</p>
              <p className="text-xs text-gray-500">cette semaine</p>
            </div>
            <Clock className="w-8 h-8 text-purple-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Conflits</p>
              <p className={`text-2xl font-bold ${hasConflicts() ? 'text-red-600' : 'text-green-600'}`}>
                {hasConflicts() ? '⚠️' : '✅'}
              </p>
              <p className="text-xs text-gray-500">planning</p>
            </div>
            {hasConflicts() ? (
              <AlertTriangle className="w-8 h-8 text-red-400" />
            ) : (
              <Check className="w-8 h-8 text-green-400" />
            )}
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={previousWeek}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {format(weekStart, 'dd MMMM', { locale: fr })} - {format(weekEnd, 'dd MMMM yyyy', { locale: fr })}
            </h2>
            <button
              onClick={nextWeek}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                view === 'week' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Semaine
            </button>
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                view === 'month' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Mois
            </button>
          </div>
        </div>

        {/* Week View */}
        {view === 'week' && (
          <div className="grid grid-cols-8 gap-1">
            {/* Time column */}
            <div className="space-y-1">
              <div className="h-12" /> {/* Header spacer */}
              {Array.from({ length: 10 }, (_, i) => i + 8).map(hour => (
                <div key={hour} className="h-16 flex items-center justify-center text-xs text-gray-500 border-r border-gray-100">
                  {hour}:00
                </div>
              ))}
            </div>

            {/* Days columns */}
            {weekDays.map((day) => {
              const dayMeetings = getMeetingsForDay(day);
              const timeSlots = getTimeSlots(day);
              
              return (
                <div key={day.toISOString()} className="space-y-1">
                  {/* Day header */}
                  <div className={`h-12 flex flex-col items-center justify-center rounded-lg transition-colors ${
                    isToday(day) 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}>
                    <div className="text-xs font-medium">
                      {format(day, 'EEE', { locale: fr })}
                    </div>
                    <div className="text-lg font-bold">
                      {format(day, 'd')}
                    </div>
                  </div>

                  {/* Time slots */}
                  {Array.from({ length: 10 }, (_, i) => i + 8).map(hour => {
                    const slot = timeSlots.find(s => parseInt(s.start.split(':')[0]) === hour);
                    const meeting = slot?.meeting;
                    
                    return (
                      <div
                        key={hour}
                        className={`h-16 border border-gray-100 rounded transition-all duration-200 cursor-pointer ${
                          meeting
                            ? meeting.status === 'CONFIRME'
                              ? 'bg-green-100 border-green-300 hover:bg-green-200'
                              : 'bg-orange-100 border-orange-300 hover:bg-orange-200'
                            : slot?.available
                              ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                              : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          if (meeting) {
                            // Navigate to meeting detail
                          } else if (slot?.available) {
                            // Open create meeting modal
                          }
                        }}
                      >
                        {meeting && (
                          <div className="p-1 h-full">
                            <div className="text-xs font-medium text-gray-900 truncate">
                              {meeting.title}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center space-x-1">
                              {meeting.meeting_type === 'VIRTUAL' ? (
                                <Video className="w-3 h-3" />
                              ) : (
                                <MapPin className="w-3 h-3" />
                              )}
                              <span>{meeting.attendees?.length || 0}</span>
                            </div>
                          </div>
                        )}
                        {slot?.available && !meeting && (
                          <div className="p-1 h-full flex items-center justify-center">
                            <Plus className="w-4 h-4 text-blue-600 opacity-50" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Month View */}
        {view === 'month' && (
          <div className="grid grid-cols-7 gap-1">
            {/* Days of week header */}
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
              <div key={day} className="h-10 flex items-center justify-center text-sm font-medium text-gray-600">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {weekDays.map(day => {
              const dayMeetings = getMeetingsForDay(day);
              
              return (
                <div
                  key={day.toISOString()}
                  className={`h-24 border border-gray-100 rounded p-1 cursor-pointer transition-colors ${
                    isToday(day) ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedDate(day)}
                >
                  <div className={`text-sm font-medium ${isToday(day) ? 'text-blue-600' : 'text-gray-900'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1 mt-1">
                    {dayMeetings.slice(0, 2).map(meeting => (
                      <div
                        key={meeting.id}
                        className={`text-xs p-1 rounded truncate ${
                          meeting.status === 'CONFIRME'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        {meeting.title}
                      </div>
                    ))}
                    {dayMeetings.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{dayMeetings.length - 2} autres
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Availability Modal */}
      {showAvailabilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Gérer mes disponibilités</h2>
              <button
                onClick={() => setShowAvailabilityModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((dayName, dayIndex) => {
                  const daySlots = availability.filter(slot => slot.day === dayIndex + 1);
                  
                  return (
                    <div key={dayIndex} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">{dayName}</h3>
                        <button
                          onClick={() => {
                            setAvailability(prev => [...prev, {
                              day: dayIndex + 1,
                              start: '09:00',
                              end: '12:00'
                            }]);
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          + Ajouter créneau
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {daySlots.map((slot, slotIndex) => (
                          <div key={slotIndex} className="flex items-center space-x-3">
                            <input
                              type="time"
                              value={slot.start}
                              onChange={(e) => {
                                setAvailability(prev => prev.map(s => 
                                  s === slot ? { ...s, start: e.target.value } : s
                                ));
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-gray-500">à</span>
                            <input
                              type="time"
                              value={slot.end}
                              onChange={(e) => {
                                setAvailability(prev => prev.map(s => 
                                  s === slot ? { ...s, end: e.target.value } : s
                                ));
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <button
                              onClick={() => {
                                setAvailability(prev => prev.filter(s => s !== slot));
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        
                        {daySlots.length === 0 && (
                          <p className="text-sm text-gray-500 italic">Aucune disponibilité configurée</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAvailabilityModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    // Save availability to database
                    setShowAvailabilityModal(false);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};